import { env } from "@/env";
import { measure, normalizeUrlPath } from "@/metrics";
import type { CacheOptions } from "@/app/_api/fetchWithCache";
import { withCache } from "@/app/_api/fetchWithCache";
import type { ResolvedAuth } from "./auth";

export function getXiServerUrl(): string {
  const url = env.NEXT_PUBLIC_XI_SERVER_URL;
  if (!url) {
    throw new Error("NEXT_PUBLIC_XI_SERVER_URL is not set");
  }
  return url;
}

const DEFAULT_TIMEOUT_MS = 10_000;

export interface XiFetchOptions {
  /** Extra headers merged on top of the default `Content-Type: application/json`. */
  headers?: Record<string, string>;
  method?: string;
  /** Key-value pairs appended to the URL as query parameters. */
  searchParams?: Record<string, string>;
  /** Caller-supplied abort signal (combined with the internal timeout). */
  signal?: AbortSignal;
  /** Request timeout in milliseconds. Defaults to 10 000 ms. */
  timeoutMs?: number;
  /**
   * When provided, wraps the call with `withCache` so that both cache hits and
   * misses are recorded and a `cache` label is added to the metric.
   */
  cacheOptions?: Omit<CacheOptions, "onCacheStatus">;
  /**
   * Lazy auth resolver called inside the cache boundary — only invoked on a
   * cache miss, so auth is never resolved unnecessarily on cache hits.
   */
  authResolver?: () => Promise<ResolvedAuth>;
}

/**
 * Thin wrapper around `fetch` for ElevenLabs XI API calls.
 *
 * Provides:
 * - Automatic base-URL resolution via `NEXT_PUBLIC_XI_SERVER_URL`
 * - Timeout via `AbortController` (combined with an optional caller signal)
 * - Structured error messages with response body extraction
 * - Metrics via `measure()` — URL is known upfront so `api` and `endpoint`
 *   labels are correct on both cache hits and misses
 * - Optional caching via `cacheOptions` — adds a `cache: "hit" | "miss"` label
 * - Lazy auth via `authResolver` — resolved inside the cache boundary so auth
 *   is only fetched on cache misses
 *
 * Returns the parsed JSON body as `unknown` so each caller can narrow the type.
 */
export async function xiFetch(path: string, options: XiFetchOptions = {}): Promise<unknown> {
  if (env.NEXT_PUBLIC_DISABLE_XI_FETCHES) {
    throw new Error("XI API fetches are disabled");
  }

  const {
    headers = {},
    method = "GET",
    searchParams = {},
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    cacheOptions,
    authResolver,
  } = options;

  const url = new URL(`${getXiServerUrl()}${path}`);
  for (const [key, value] of Object.entries(searchParams)) {
    url.searchParams.set(key, value);
  }

  // Build metric labels from the known URL — correct on hits and misses alike.
  const api = url.hostname;
  const endpoint = normalizeUrlPath(url.pathname).replace(/^\//, "");

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const combinedSignal = signal ? AbortSignal.any([signal, controller.signal]) : controller.signal;

  let status: "success" | "error" = "success";
  let cache: "hit" | "miss" = "miss";

  // The actual HTTP call — only runs on cache misses (or when cacheOptions is absent).
  const doFetch = async () => {
    try {
      // Resolve auth lazily — only called on cache miss
      const resolvedAuth = authResolver ? await authResolver() : null;

      // Merge any auth search params into the URL
      let fetchUrl: URL = url;
      if (resolvedAuth?.searchParams && Object.keys(resolvedAuth.searchParams).length > 0) {
        const u = new URL(url);
        for (const [k, v] of Object.entries(resolvedAuth.searchParams)) {
          u.searchParams.set(k, v);
        }
        fetchUrl = u;
      }

      const response = await fetch(fetchUrl, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...resolvedAuth?.headers,
          ...headers,
        },
        signal: combinedSignal,
        cache: "no-store",
      });

      if (!response.ok) {
        const responseUrl = response.url;
        const errorBody = await readErrorBody(response);
        throw new Error(
          `XI API error: ${response.status} ${response.statusText} (${responseUrl}). Body: ${JSON.stringify(errorBody)}`,
        );
      }

      return await response.json();
    } catch (e) {
      status = "error";
      throw e;
    }
  };

  try {
    return await measure(
      "externalApi",
      () => ({ api, endpoint, status, ...(cacheOptions ? { cache } : {}) }),
      cacheOptions
        ? () =>
            withCache(doFetch, {
              ...cacheOptions,
              onCacheStatus: s => {
                cache = s;
              },
            })
        : doFetch,
    );
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error(`XI API request aborted after ${timeoutMs}ms`);
    }

    if (err instanceof Error) {
      throw err;
    }

    throw new Error("Unknown error during XI API request");
  } finally {
    // Always clear: on cache hits doFetch never runs, so the timeout must be
    // cleared here to avoid firing the AbortController on a stale request.
    clearTimeout(timeoutId);
  }
}

export function isXiApiUrl(url: string): boolean {
  return url.includes(getXiServerUrl());
}

async function readErrorBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    try {
      return await response.text();
    } catch {
      return null;
    }
  }
}
